// AuthErrorLayout — shared visual + a11y skeleton for every dedicated
// recovery route under /auth/* (ADR-018).
//
// Requirements (verified by route-level tests in src/test/auth-recovery.test.tsx):
//   - role="alert" so screen readers announce the page on mount
//   - aria-live="polite" so subsequent edits (e.g. "sending…" status) are
//     announced without preempting other speech
//   - Brandmark for visual continuity with the rest of the portal
//   - Three CTAs (per issue #4 spec): primary, secondary, tertiary
//   - Programmatic focus-on-mount on the heading so keyboard users land in
//     a sensible place

import { useEffect, useRef, type ReactNode } from 'react';
import brandmarkBlack from '../../assets/solomon-brandmark-black.svg';

export interface AuthErrorCta {
  /** Visual label */
  label: string;
  /** Click handler. If `href` is set this is treated as a link. */
  onClick?: () => void;
  /** Optional href; when set the CTA renders as `<a>` not `<button>`. */
  href?: string;
}

export interface AuthErrorLayoutProps {
  /** Headline (also receives focus on mount). */
  title: string;
  /** Body copy. Plain text only — no HTML strings. */
  description: ReactNode;
  /** Primary call-to-action (orange button). */
  primary: AuthErrorCta;
  /** Secondary CTA (outline button). Optional. */
  secondary?: AuthErrorCta;
  /** Tertiary CTA (text link, e.g. mailto). Optional. */
  tertiary?: AuthErrorCta;
  /** Optional inline status text rendered below the CTAs (aria-live). */
  status?: string;
}

function CtaButton({
  cta,
  variant,
}: {
  cta: AuthErrorCta;
  variant: 'primary' | 'secondary';
}): ReactNode {
  const cls =
    variant === 'primary'
      ? 'inline-flex items-center justify-center rounded-sm bg-orange px-5 py-2.5 text-[14px] font-medium text-white transition-colors hover:bg-orange-deep'
      : 'inline-flex items-center justify-center rounded-sm border border-border-strong bg-white px-5 py-2.5 text-[14px] font-medium text-text-dark transition-colors hover:bg-cream';
  if (cta.href) {
    return (
      <a className={cls} href={cta.href}>
        {cta.label}
      </a>
    );
  }
  return (
    <button type="button" className={cls} onClick={cta.onClick}>
      {cta.label}
    </button>
  );
}

export function AuthErrorLayout({
  title,
  description,
  primary,
  secondary,
  tertiary,
  status,
}: AuthErrorLayoutProps): ReactNode {
  const headingRef = useRef<HTMLHeadingElement | null>(null);

  useEffect(() => {
    // Move focus to the heading so screen readers + keyboard users land here
    // on mount. Heading is tabIndex={-1} so focus succeeds without making it
    // tab-reachable afterwards.
    headingRef.current?.focus();
  }, []);

  return (
    <main
      id="main-content"
      tabIndex={-1}
      className="flex min-h-screen flex-col items-center justify-center gap-4 bg-cream px-6 py-12 text-center focus:outline-none"
    >
      <div role="alert" aria-live="polite" className="contents">
        <img src={brandmarkBlack} alt="Solomon Advising" className="mb-2 h-12 w-auto" />
        <h1 ref={headingRef} tabIndex={-1} className="text-[24px] focus:outline-none">
          {title}
        </h1>
        <div className="max-w-prose text-text-body">{description}</div>
        <div className="mt-2 flex flex-wrap items-center justify-center gap-3">
          <CtaButton cta={primary} variant="primary" />
          {secondary ? <CtaButton cta={secondary} variant="secondary" /> : null}
        </div>
        {tertiary ? (
          <p className="mt-2 text-[12px] text-text-muted">
            {tertiary.href ? (
              <a className="underline hover:text-orange" href={tertiary.href}>
                {tertiary.label}
              </a>
            ) : (
              <button
                type="button"
                className="underline hover:text-orange"
                onClick={tertiary.onClick}
              >
                {tertiary.label}
              </button>
            )}
          </p>
        ) : null}
        {status ? (
          <p
            aria-live="polite"
            className="mt-2 text-[13px] text-text-muted"
            data-testid="auth-error-status"
          >
            {status}
          </p>
        ) : null}
      </div>
    </main>
  );
}
